import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Play, Pause, ArrowRight, Mail, Instagram, Twitter, Youtube, Music2, Lock, ShoppingCart, Loader2 } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import { getFileUrl } from "@/components/FileUpload";
import { toast } from "sonner";
import useInView from "@/hooks/useInView";
import { useCheckout } from "@/components/CheckoutModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCIAL_ICONS = { instagram: Instagram, twitter: Twitter, youtube: Youtube, spotify: Music2 };
const HOME_PREVIEW_LIMIT = 30;

function resolveImage(val) {
  if (!val || !val.trim()) return "";
  const v = val.trim();
  if (v.startsWith("http")) return v;
  return getFileUrl(v);
}

function LatestReleasePlayer({ src, isPaid }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [locked, setLocked] = useState(false);

  const stop = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setPlaying(false);
    setProgress(0);
  };

  const toggle = () => {
    if (locked) return;
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    const t = audioRef.current.currentTime;
    const d = audioRef.current.duration;
    if (isPaid && t >= HOME_PREVIEW_LIMIT) {
      audioRef.current.pause();
      audioRef.current.currentTime = HOME_PREVIEW_LIMIT;
      setPlaying(false);
      setLocked(true);
      setProgress(100);
      return;
    }
    const maxTime = isPaid ? Math.min(d, HOME_PREVIEW_LIMIT) : d;
    setProgress(maxTime > 0 ? (t / maxTime) * 100 : 0);
  };

  const seek = (e) => {
    if (!audioRef.current?.duration || locked) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const maxTime = isPaid ? Math.min(audioRef.current.duration, HOME_PREVIEW_LIMIT) : audioRef.current.duration;
    audioRef.current.currentTime = pct * maxTime;
  };

  const fmt = (s) => { const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${m}:${sec.toString().padStart(2, "0")}`; };

  const displayDuration = isPaid ? Math.min(duration, HOME_PREVIEW_LIMIT) : duration;
  const displayTime = isPaid ? Math.min(audioRef.current?.currentTime || 0, HOME_PREVIEW_LIMIT) : (audioRef.current?.currentTime || 0);

  return (
    <div data-testid="latest-release-player" className="w-full">
      <audio
        ref={audioRef}
        src={getFileUrl(src)}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={() => { if (isPaid) setLocked(true); setPlaying(false); setProgress(isPaid ? 100 : 0); }}
      />

      {locked ? (
        <div data-testid="latest-release-lock-overlay" className="flex items-center gap-4 bg-white/[0.03] border border-white/5 rounded-lg p-4">
          <div className="w-14 h-14 rounded-full bg-[#8B0000]/20 border border-[#8B0000]/30 flex items-center justify-center flex-shrink-0">
            <Lock className="w-5 h-5 text-[#8B0000]" />
          </div>
          <div className="flex-1">
            <p data-testid="latest-release-unlock-message" className="text-sm font-semibold text-white/80">Unlock Full Track</p>
            <p className="text-xs text-white/30 mt-0.5">Preview ended · Purchase to hear the full track</p>
          </div>
          <button
            data-testid="latest-release-replay-btn"
            onClick={() => { setLocked(false); stop(); }}
            className="text-xs text-[#D4AF37] hover:text-white transition-colors uppercase tracking-wider font-semibold flex-shrink-0"
          >
            Replay
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <button onClick={toggle} data-testid="latest-release-play-btn" className="w-14 h-14 rounded-full bg-[#D4AF37] flex items-center justify-center hover:bg-white hover:scale-105 transition-all duration-300 flex-shrink-0">
            {playing ? <Pause className="w-6 h-6 text-black" /> : <Play className="w-6 h-6 text-black ml-1" />}
          </button>
          <div className="flex-1">
            <div className="relative h-1.5 bg-white/10 rounded-full cursor-pointer group" onClick={seek}>
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] rounded-full transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
              <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-[#D4AF37] rounded-full shadow-[0_0_8px_rgba(212,175,55,0.5)] opacity-0 group-hover:opacity-100 transition-opacity" style={{ left: `calc(${Math.min(progress, 100)}% - 7px)` }} />
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-[11px] text-white/40 font-medium tabular-nums">{fmt(displayTime)}</span>
              <div className="flex items-center gap-2">
                {isPaid && <span className="text-[9px] text-[#8B0000]/60 uppercase tracking-wider">{HOME_PREVIEW_LIMIT}s preview</span>}
                <span className="text-[11px] text-white/40 font-medium tabular-nums">{displayDuration > 0 ? fmt(displayDuration) : "--:--"}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LatestReleaseHero({ track, onBuy }) {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef);
  const coverUrl = resolveImage(track.cover_art);
  const base = isInView ? "" : "opacity-0";

  return (
    <section
      ref={sectionRef}
      id="latest-release"
      data-testid="latest-release-section"
      className="relative min-h-[85vh] flex items-center overflow-hidden"
    >
      {/* Blurred artwork background */}
      <div className="absolute inset-0">
        {coverUrl ? (
          <div
            className="absolute inset-0 bg-cover bg-center scale-110 blur-[60px]"
            style={{ backgroundImage: `url(${coverUrl})` }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a0a0a] via-[#0d0d0d] to-[#0a0a1a]" />
        )}
        <div className="absolute inset-0 bg-[#050505]/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/80" />
      </div>

      {/* Subtle gold accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28">
        {/* Section label */}
        <div className={`mb-10 transition-all duration-700 ${isInView ? "animate-fade-in-up" : base}`} style={{ animationDelay: "0.1s" }}>
          <p className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold">New Music</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Artwork — left side */}
          <div className={`lg:col-span-5 transition-all duration-1000 ${isInView ? "animate-fade-in-left" : base}`} style={{ animationDelay: "0.2s" }}>
            <div className="relative">
              {/* Glow behind artwork */}
              <div className="absolute -inset-4 bg-[#D4AF37]/5 rounded-sm blur-2xl" />
              <div className="relative aspect-square overflow-hidden border border-[#D4AF37]/20 pulse-glow">
                {coverUrl ? (
                  <img
                    src={coverUrl}
                    alt={track.title}
                    className="w-full h-full object-cover transition-transform duration-[2s] hover:scale-[1.03]"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
                    <Music2 className="w-16 h-16 text-[#D4AF37]/20" />
                  </div>
                )}
                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#D4AF37]/50" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37]/50" />
              </div>
              {/* Exclusive badge */}
              {track.exclusive && (
                <div
                  className={`absolute -top-3 -right-3 sm:top-4 sm:right-4 z-10 transition-all duration-700 ${isInView ? "animate-scale-reveal" : base}`}
                  style={{ animationDelay: "0.8s" }}
                >
                  <div data-testid="latest-release-exclusive-badge" className="flex items-center gap-1.5 bg-[#8B0000] text-white pl-2.5 pr-3 py-1.5 text-[10px] uppercase font-bold tracking-widest shadow-lg shadow-[#8B0000]/30">
                    <Lock className="w-3 h-3" /> Exclusive
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Details — right side */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <p
              className={`text-xs uppercase tracking-[0.3em] text-white/30 mb-3 transition-all duration-700 ${isInView ? "animate-fade-in-right" : base}`}
              style={{ animationDelay: "0.3s" }}
            >
              {track.artist_name || "Luxifer 6"}
            </p>

            <h2
              className={`font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mb-3 transition-all duration-700 ${isInView ? "animate-fade-in-right" : base}`}
              style={{ animationDelay: "0.4s" }}
            >
              {track.title}
            </h2>

            <div
              className={`flex items-center gap-4 mb-6 transition-all duration-700 ${isInView ? "animate-fade-in-right" : base}`}
              style={{ animationDelay: "0.5s" }}
            >
              <div className="h-[1px] w-12 bg-[#D4AF37]/50" />
              <span className="text-[#D4AF37] text-xs uppercase tracking-widest font-semibold">
                {track.release_date ? `Released ${track.release_date}` : "Latest Release"}
              </span>
            </div>

            {track.description && (
              <p
                className={`text-white/45 text-base sm:text-lg leading-relaxed mb-8 max-w-xl transition-all duration-700 ${isInView ? "animate-fade-in-right" : base}`}
                style={{ animationDelay: "0.55s" }}
              >
                {track.description}
              </p>
            )}

            {/* Audio player */}
            {track.audio_preview && (
              <div
                className={`mb-8 max-w-md transition-all duration-700 ${isInView ? "animate-fade-in-right" : base}`}
                style={{ animationDelay: "0.6s" }}
              >
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/25 mb-3 font-medium">Preview</p>
                <LatestReleasePlayer src={track.audio_preview} isPaid={track.price > 0} />
              </div>
            )}

            {/* Action buttons */}
            <div
              className={`flex flex-wrap gap-4 transition-all duration-700 ${isInView ? "animate-fade-in-right" : base}`}
              style={{ animationDelay: "0.7s" }}
            >
              {track.audio_preview && (
                <Button
                  data-testid="latest-release-play-preview-btn"
                  onClick={() => {
                    const btn = document.querySelector('[data-testid="latest-release-play-btn"]');
                    btn?.click();
                  }}
                  variant="outline"
                  className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] font-semibold px-7 py-3 uppercase tracking-wider text-xs h-auto transition-all duration-300"
                >
                  <Play className="w-4 h-4 mr-2" /> Play Preview
                </Button>
              )}
              {track.price > 0 && (
                <Button
                  data-testid="latest-release-buy-btn"
                  onClick={() => onBuy(track.id)}
                  className="bg-[#D4AF37] text-black hover:bg-white font-semibold px-7 py-3 uppercase tracking-wider text-xs h-auto transition-all duration-300"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" /> Buy Now — ${track.price.toFixed(2)}
                </Button>
              )}
              <Button
                asChild
                variant="outline"
                className="border-white/15 text-white/60 hover:border-white/40 hover:text-white hover:bg-white/5 px-7 py-3 uppercase tracking-wider text-xs h-auto transition-all duration-300"
              >
                <Link to="/music">All Music <ArrowRight className="w-3.5 h-3.5 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gold accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
    </section>
  );
}

function LatestReleaseFallback({ releaseRef }) {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef);
  const base = isInView ? "" : "opacity-0";

  return (
    <section
      ref={sectionRef}
      id="latest-release"
      data-testid="latest-release-section"
      className="relative py-28 sm:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-transparent" />
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />
      <div ref={releaseRef} className="relative z-10 max-w-6xl mx-auto px-6">
        <div className={`transition-all duration-700 ${isInView ? "animate-fade-in-up" : base}`} style={{ animationDelay: "0.1s" }}>
          <p className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold mb-3">New Music</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className={`relative aspect-square overflow-hidden border border-[#D4AF37]/20 transition-all duration-1000 ${isInView ? "animate-fade-in-left" : base}`} style={{ animationDelay: "0.3s" }}>
            <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
              <Music2 className="w-16 h-16 text-[#D4AF37]/20" />
            </div>
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#D4AF37]/50" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37]/50" />
          </div>
          <div className={`flex flex-col justify-center transition-all duration-700 ${isInView ? "animate-fade-in-right" : base}`} style={{ animationDelay: "0.5s" }}>
            <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl mb-3">COMING SOON</h2>
            <div className="flex items-center gap-4 mb-6">
              <div className="h-[1px] w-12 bg-[#D4AF37]/50" />
              <span className="text-[#D4AF37] text-xs uppercase tracking-widest font-semibold">New Release</span>
            </div>
            <p className="text-white/45 text-lg leading-relaxed mb-8 max-w-xl">The next chapter is being written. Stay tuned for the latest release from Luxifer 6.</p>
            <Button asChild variant="outline" className="border-white/15 text-white/60 hover:border-white/40 hover:text-white hover:bg-white/5 px-7 py-3 uppercase tracking-wider text-xs h-auto w-fit">
              <Link to="/music">View Discography <ArrowRight className="w-3.5 h-3.5 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />
    </section>
  );
}

export default function Home() {
  const releaseRef = useRef(null);
  const [hp, setHp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [featuredMusic, setFeaturedMusic] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const { startCheckout, modal } = useCheckout();

  useEffect(() => {
    const ts = `_t=${Date.now()}`;
    Promise.all([
      axios.get(`${API}/homepage?${ts}`).then(r => r.data).catch(() => null),
      axios.get(`${API}/music?featured=true&${ts}`).then(r => r.data).catch(() => []),
      axios.get(`${API}/videos?featured=true&${ts}`).then(r => r.data).catch(() => []),
      axios.get(`${API}/images?${ts}`).then(r => r.data).catch(() => []),
    ]).then(([hpData, music, videos, images]) => {
      setHp(hpData || {});
      setFeaturedMusic(music);
      setFeaturedVideos(videos);
      setGalleryImages(images.slice(0, 8));
      setLoading(false);
    });
  }, []);

  // All content comes from API only — no hardcoded defaults
  const hero = hp?.hero || {};
  const about = hp?.about || {};
  const label = hp?.label || {};
  const contact = hp?.contact || {};

  const heroBg = resolveImage(hero.background_image);
  const latestTrack = featuredMusic[0];

  // Show loading skeleton while fetching
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* ── Hero ── */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {heroBg ? (
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroBg})` }} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0d0000] via-[#050505] to-[#000a0d]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/50 to-transparent" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          {hero.label_text && (
            <p className="text-sm uppercase tracking-[0.4em] text-[#D4AF37] mb-6 animate-fade-in-up opacity-0" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
              {hero.label_text}
            </p>
          )}
          <h1 className="font-heading text-7xl sm:text-8xl lg:text-9xl text-white mb-4 animate-fade-in-up opacity-0 leading-none" style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}>
            {hero.title || "LUXIFER 6"}
          </h1>
          {hero.tagline && (
            <p className="font-heading text-3xl sm:text-4xl lg:text-5xl gold-shimmer mb-6 animate-fade-in-up opacity-0" style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}>
              {hero.tagline}
            </p>
          )}
          {hero.subtext && (
            <p className="text-white/50 text-base sm:text-lg max-w-xl mx-auto mb-10 animate-fade-in-up opacity-0" style={{ animationDelay: "0.8s", animationFillMode: "forwards" }}>
              {hero.subtext}
            </p>
          )}
          <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up opacity-0" style={{ animationDelay: "1s", animationFillMode: "forwards" }}>
            <Button data-testid="hero-listen-btn" onClick={() => releaseRef.current?.scrollIntoView({ behavior: "smooth" })} className="bg-[#D4AF37] text-black hover:bg-white font-semibold px-8 py-3 uppercase tracking-wider text-sm h-auto">Listen Now</Button>
            <Button data-testid="hero-videos-btn" asChild variant="outline" className="border-white/20 text-white hover:border-white hover:bg-white/5 px-8 py-3 uppercase tracking-wider text-sm h-auto"><Link to="/videos">Watch Videos</Link></Button>
            <Button data-testid="hero-contact-btn" asChild variant="outline" className="border-white/20 text-white hover:border-white hover:bg-white/5 px-8 py-3 uppercase tracking-wider text-sm h-auto"><Link to="/contact">Contact</Link></Button>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce"><div className="w-[1px] h-12 bg-gradient-to-b from-transparent to-[#D4AF37]/50" /></div>
      </section>

      {/* ── Latest Release ── */}
      <div ref={releaseRef}>
        {latestTrack ? (
          <LatestReleaseHero track={latestTrack} onBuy={startCheckout} />
        ) : (
          <LatestReleaseFallback releaseRef={releaseRef} />
        )}
      </div>

      {/* ── Featured Videos ── */}
      <AnimatedSection testId="featured-videos-section" className="py-24 sm:py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">Visual Content</p>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl">FEATURED VIDEOS</h2>
            </div>
            <Link to="/videos" data-testid="view-all-videos-link" className="hidden sm:flex items-center gap-2 text-sm text-[#D4AF37] hover:text-white transition-colors uppercase tracking-wider">View All <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredVideos.length > 0 ? featuredVideos.slice(0, 3).map((video) => (
              <div key={video.id} data-testid={`featured-video-${video.id}`} className="group relative aspect-video bg-[#111111] border border-white/5 overflow-hidden card-glow cursor-pointer">
                {video.youtube_embed_url ? (
                  <iframe src={video.youtube_embed_url} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={video.title} />
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10" />
                    {video.thumbnail ? <img src={getFileUrl(video.thumbnail)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}
                    <div className="absolute inset-0 flex items-center justify-center z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 rounded-full bg-[#D4AF37]/90 flex items-center justify-center"><Play className="w-7 h-7 text-black ml-1" /></div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
                      <p className="text-white text-sm font-semibold">{video.title}</p>
                    </div>
                    <div className="absolute inset-0 bg-[#111111] flex items-center justify-center"><Play className="w-8 h-8 text-white/10" /></div>
                  </>
                )}
              </div>
            )) : (
              <div className="col-span-full text-center py-8 text-white/20"><p>Videos coming soon.</p></div>
            )}
          </div>
        </div>
      </AnimatedSection>

      {/* ── About ── */}
      <AnimatedSection testId="about-section" className="py-24 sm:py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#050505] to-[#0a0508] opacity-50" />
        <div className="max-w-4xl mx-auto relative z-10">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">The Story</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-8">{about.title || "ABOUT"}</h2>
          <div className="space-y-6">
            {(about.paragraphs && about.paragraphs.length > 0 ? about.paragraphs : []).map((p, i) => (
              <p key={i} className="text-white/60 text-base sm:text-lg leading-relaxed">{p}</p>
            ))}
            {about.quote && (
              <div className="pt-4"><span className="font-heading text-2xl text-[#D4AF37]">"{about.quote}"</span></div>
            )}
          </div>
        </div>
      </AnimatedSection>

      {/* ── Label ── */}
      <AnimatedSection testId="label-section" className="py-24 sm:py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#8B0000]/10 to-transparent" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="border-l-4 border-[#8B0000] pl-8">
            <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">The Label</p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-8">{label.title || "THE LABEL"}</h2>
            {label.description && (
              <p className="text-white/60 text-base sm:text-lg leading-relaxed mb-6">{label.description}</p>
            )}
            <Button data-testid="label-learn-more-btn" asChild variant="outline" className="border-[#8B0000]/50 text-white hover:border-[#8B0000] hover:bg-[#8B0000]/10 uppercase tracking-wider text-sm px-8 py-3 h-auto">
              <Link to="/label">Learn More <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </AnimatedSection>

      {/* ── Gallery Preview ── */}
      {galleryImages.length > 0 && (
        <AnimatedSection testId="gallery-preview-section" className="py-24 sm:py-32 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">Visuals</p>
                <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl">GALLERY</h2>
              </div>
              <Link to="/gallery" className="hidden sm:flex items-center gap-2 text-sm text-[#D4AF37] hover:text-white transition-colors uppercase tracking-wider">View All <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {galleryImages.map((img) => (
                <Link key={img.id} to="/gallery" className="relative aspect-square overflow-hidden bg-[#111111] border border-white/5 hover:border-[#D4AF37]/30 group transition-all duration-500">
                  <img src={getFileUrl(img.storage_path)} alt={img.title || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500" />
                </Link>
              ))}
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* ── Contact CTA ── */}
      <AnimatedSection testId="contact-cta-section" className="py-24 sm:py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">Get In Touch</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-6">LET'S CONNECT</h2>
          <p className="text-white/50 text-base sm:text-lg mb-10">For bookings, features, collaborations, and business inquiries.</p>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Button data-testid="contact-cta-btn" asChild className="bg-[#D4AF37] text-black hover:bg-white font-semibold px-8 py-3 uppercase tracking-wider text-sm h-auto">
              <Link to="/contact"><Mail className="w-4 h-4 mr-2" /> Contact Us</Link>
            </Button>
          </div>
          <div className="flex justify-center gap-6">
            {(contact.social_links && contact.social_links.length > 0 ? contact.social_links : []).map((social) => {
              const Icon = SOCIAL_ICONS[social.platform] || Music2;
              return (
                <a key={social.platform} href={social.url || "#"} data-testid={`home-social-${social.platform}`} className="w-11 h-11 border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300 text-white/40">
                  <Icon className="w-5 h-5" />
                </a>
              );
            })}
          </div>
        </div>
      </AnimatedSection>
      {modal}
    </div>
  );
}
